import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/lib/auth";
import { ThemeProvider, THEME_BOOTSTRAP_SCRIPT } from "@/lib/theme";


import appCss from "../styles.css?url";

function NotFoundComponent() {
  return (
    <div className="min-h-screen grid place-items-center bg-slate-50 p-4">
      <div className="text-center max-w-md">
        <div className="text-8xl font-bold text-slate-200 mb-4">404</div>
        <h1 className="text-2xl font-semibold mb-2">Trang không tồn tại</h1>
        <p className="text-slate-500 mb-6">Đường dẫn bạn tìm không có hoặc đã bị di chuyển.</p>
        <Link
          to="/"
          className="inline-flex items-center justify-center rounded-md bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
        >
          Về trang chủ
        </Link>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight">Trang chưa tải được</h1>
        <p className="mt-2 text-sm text-muted-foreground">Đã có lỗi xảy ra.</p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => { router.invalidate(); reset(); }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
          >Thử lại</button>
          <a href="/" className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium">Về trang chủ</a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "google-site-verification", content: "2nf_uDJlcLBBrj8TIfVDDm49PS2MVwltCxD1yzlBZ4k" },
      { title: "Ezyclass — Hệ thống quản lý trung tâm học tập" },
      { name: "description", content: "Phần mềm quản lý trung tâm học tập dành cho Việt Nam. Điểm danh 1 click, giao bài, báo cáo tự động." },
      { property: "og:title", content: "Ezyclass — Hệ thống quản lý trung tâm học tập" },
      { property: "og:description", content: "Phần mềm quản lý trung tâm học tập dành cho Việt Nam. Điểm danh 1 click, giao bài, báo cáo tự động." },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Ezyclass" },
      { name: "twitter:title", content: "Ezyclass — Hệ thống quản lý trung tâm học tập" },
      { name: "twitter:description", content: "Phần mềm quản lý trung tâm học tập dành cho Việt Nam. Điểm danh 1 click, giao bài, báo cáo tự động." },
      { property: "og:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/attachments/og-images/706b2e2e-077c-477b-8fb5-a0a32aced755" },
      { name: "twitter:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/attachments/og-images/706b2e2e-077c-477b-8fb5-a0a32aced755" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", type: "image/png", href: "/favicon.png" },
      { rel: "apple-touch-icon", href: "/favicon.png" },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Sora:wght@500;600;700&display=swap",
      },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Ezyclass",
          url: "https://ezyclass.com",
          logo: "https://ezyclass.com/favicon.png",
          description:
            "Phần mềm quản lý trung tâm học tập dành cho Việt Nam.",
          sameAs: ["https://ezyclass.lovable.app"],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "Ezyclass",
          url: "https://ezyclass.com",
          potentialAction: {
            "@type": "SearchAction",
            target: "https://ezyclass.com/?q={search_term_string}",
            "query-input": "required name=search_term_string",
          },
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="vi">
      <head>
        <HeadContent />
        <script dangerouslySetInnerHTML={{ __html: THEME_BOOTSTRAP_SCRIPT }} />
      </head>
      <body>{children}<Scripts /></body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <Outlet />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
